import axios from 'axios'

// ==========================================
// 1. ZRX 客户端单例 (https://stock.ziruxing.com)
// ==========================================
const zrxClient = axios.create({
    timeout: 10000,
    baseURL: 'https://stock.ziruxing.com'
})
zrxClient.interceptors.request.use(
    config => config,
    err => {
        console.log(err)
        return Promise.reject(err)
    }
)
zrxClient.interceptors.response.use(
    res => res,
    err => {
        console.log(err)
        return Promise.reject(err)
    }
)

export function request_zrx(config) {
    return zrxClient(config)
}

// ==========================================
// 2. ZZQUANT 客户端单例 (https://api.zizizaizai.com)
// ==========================================
const zzquantClient = axios.create({
    timeout: 20000,
    baseURL: 'https://api.zizizaizai.com'
})

// 请求拦截器
zzquantClient.interceptors.request.use(
    config => {
        // 自动附加登录令牌（若存在）
        try {
            const token = localStorage.getItem('token')
            if (token) {
                if (!config.headers) config.headers = {}
                config.headers['Authorization'] = `Bearer ${token}`
            }
        } catch (e) {
            // 忽略localStorage异常
        }
        // 自动附加策略分享码（若存在）
        try {
            const shareCode = localStorage.getItem('strategy_share_code')
            if (shareCode) {
                if (!config.headers) config.headers = {}
                config.headers['X-Strategy-Share-Code'] = shareCode
            }
        } catch (e) {
            // 忽略localStorage异常
        }
        return config
    },
    err => {
        console.log(err)
        return Promise.reject(err)
    }
)

// 响应拦截器
zzquantClient.interceptors.response.use(
    res => {
        // 自动更新 Token 逻辑 (针对旧版 Token 补全昵称)
        const updateToken = res.headers && res.headers['x-update-token']
        if (updateToken) {
            try {
                localStorage.setItem('token', updateToken)
                console.log('[Auth] Token has been automatically updated with latest user info.')
            } catch (e) {
                // 静默失败
            }
        }
        // 统一处理后端返回的未授权提示
        try {
            const data = res && res.data ? res.data : {}
            const code = data.code
            const msg = (data.message || '').toString()
            // 后端常用失败码为50000，搭配"无权限"/"验证失败"等提示
            const needLoginHints = ['无权限', '未登录', '验证失败', 'Unauthorized', '需要登录', '用户不存在']
            const needLogin = code !== 20000 && needLoginHints.some(h => msg.indexOf(h) !== -1)
            const noRedirect = res && res.config && res.config.noRedirect
            if (needLogin) {
                // 清除无效 Token
                try { localStorage.removeItem('token') } catch (e) { /* ignore */ }
                
                // 跳转到登录页，保留当前跳转来源
                if (!noRedirect) {
                    try {
                        const router = require('../router').default
                        const current = router.currentRoute && router.currentRoute.fullPath ? router.currentRoute.fullPath : '/'
                        if (router.currentRoute && router.currentRoute.path !== '/login') {
                            router.push({ path: '/login', query: { redirect: current } })
                        }
                    } catch (e) {
                        // 路由不可用时静默
                    }
                }
                return Promise.reject({ __unauthorized: true, message: msg || '未授权', code })
            }
        } catch (e) {
            // 忽略解析错误，正常返回
        }
        return res
    },
    err => {
        // 处理HTTP层面的未授权
        try {
            const status = err && err.response ? err.response.status : null
            const noRedirect = err && err.config && err.config.noRedirect

            // 检查 422 错误中的 Token 格式问题 (Flask-JWT-Extended 4.x 升级导致)
            const data = err && err.response && err.response.data ? err.response.data : {}
            const msg = (data.msg || data.message || '').toString()
            const isTokenError = status === 422 && msg.indexOf('Subject must be a string') !== -1

            if (status === 401 || isTokenError) {
                // 无论是否重定向，只要服务端返回 401，说明本地 Token 已失效/过期，立即清理，避免轮询死循环
                try {
                    localStorage.removeItem('token')
                    localStorage.removeItem('refresh_token')
                    localStorage.removeItem('user_info')
                    window.dispatchEvent(new CustomEvent('user-logout'))
                } catch (e) { /* ignore */ }

                if (!noRedirect) {
                    try {
                        const router = require('../router').default
                        const current = router.currentRoute && router.currentRoute.fullPath ? router.currentRoute.fullPath : '/'
                        if (router.currentRoute && router.currentRoute.path !== '/login') {
                            router.push({ path: '/login', query: { redirect: current } })
                        }

                        // 中断 Promise 链，防止组件报错，等待页面跳转
                        return new Promise(() => { })
                    } catch (e) {
                        // 路由不可用时静默
                    }
                }
            }
        } catch (e) {
            // 忽略跳转异常
        }
        console.log(err)
        const errData = err && err.response && err.response.data ? err.response.data : null
        const detailMsg = (errData && (errData.detail || errData.msg || errData.message)) || (err && err.message) || '请求错误'
        return Promise.reject({
            __unauthorized: (err && err.response && err.response.status === 401) || false,
            message: detailMsg,
            detail: errData && errData.detail,
            data: errData,
            response: err && err.response,
            status: err && err.response ? err.response.status : null
        })
    }
)

// ==========================================
// 3. 高频只读接口前端双级缓存与并发去重 (In-Flight & Multi-Tier Cache)
// ==========================================
const apiCache = new Map() // 一级内存缓存: key -> { expireAt, data }
const inflightRequests = new Map() // 并发去重: key -> Promise

// 历史固定交易日区间默认缓存 12 小时；普通接口默认缓存 5 分钟；动态最新交易日仅缓存 2 分钟
const TRADE_DAYS_CACHE_TTL = 12 * 60 * 60 * 1000
const DYNAMIC_TRADE_DAYS_CACHE_TTL = 2 * 60 * 1000
const DEFAULT_CACHE_TTL = 5 * 60 * 1000

// 启动时主动清理存量的旧格式动态交易日坏缓存，防止跨日或盘前访问残留 12 小时昨天的日期
try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i)
        if (k && k.startsWith('__api_cache_') && k.indexOf('/market/trade/days') !== -1 && k.indexOf('_post9') === -1 && k.indexOf('_pre9') === -1) {
            sessionStorage.removeItem(k)
        }
    }
} catch (e) {
    // 忽略异常
}

function getCacheKey(config) {
    const url = config.url || ''
    const params = config.params ? JSON.stringify(config.params) : ''
    const isTradeDaysApi = typeof url === 'string' && url.indexOf('/market/trade/days') !== -1
    if (isTradeDaysApi) {
        // 如果未指定具体历史 day_end，按当天自然日期及 09:00 开盘判定分水岭划分缓存空间
        const hasFixedHistoryEnd = (url.indexOf('day_end=') !== -1 || (config.params && config.params.day_end))
        if (!hasFixedHistoryEnd) {
            const now = new Date()
            const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
            const phase = now.getHours() >= 9 ? 'post9' : 'pre9'
            return `${config.method || 'get'}:${url}:${params}:${todayStr}_${phase}`
        }
    }
    return `${config.method || 'get'}:${url}:${params}`
}

function getStoredCache(key) {
    try {
        const item = sessionStorage.getItem('__api_cache_' + key)
        if (item) {
            const parsed = JSON.parse(item)
            if (parsed && parsed.expireAt > Date.now()) {
                return parsed.data
            }
            sessionStorage.removeItem('__api_cache_' + key)
        }
    } catch (e) {
        // 忽略存储读取异常
    }
    return null
}

function setStoredCache(key, data, expireAt) {
    try {
        sessionStorage.setItem('__api_cache_' + key, JSON.stringify({ expireAt, data }))
    } catch (e) {
        // 忽略配额超限等异常
    }
}

export function request_zzquant(config) {
    if (!config) return Promise.reject(new Error('Invalid config'))
    
    const url = config.url || ''
    // 对高频稳定的交易日接口默认开启双级缓存与去重
    const isTradeDaysApi = typeof url === 'string' && url.indexOf('/market/trade/days') !== -1
    const shouldCache = (isTradeDaysApi || config.cache) && !config.noCache && (!config.method || config.method.toLowerCase() === 'get')

    if (shouldCache) {
        const cacheKey = getCacheKey(config)
        const now = Date.now()
        // 动态最新交易日（未指定固定 day_end）使用 2 分钟短缓存；固定区间才使用 12 小时长缓存
        const isDynamicTradeDays = isTradeDaysApi && !(url.indexOf('day_end=') !== -1 || (config.params && config.params.day_end))
        const ttl = isDynamicTradeDays ? DYNAMIC_TRADE_DAYS_CACHE_TTL : (isTradeDaysApi ? TRADE_DAYS_CACHE_TTL : (config.ttl || DEFAULT_CACHE_TTL))
        
        // 1. 一级缓存：内存 Map（响应极快）
        const cached = apiCache.get(cacheKey)
        if (cached && cached.expireAt > now) {
            return Promise.resolve(JSON.parse(JSON.stringify(cached.data)))
        }

        // 2. 二级缓存：sessionStorage（页面刷新或重新进入组件依然有效）
        const storedData = getStoredCache(cacheKey)
        if (storedData) {
            // 回填一级内存缓存
            apiCache.set(cacheKey, { expireAt: now + ttl, data: storedData })
            return Promise.resolve(JSON.parse(JSON.stringify(storedData)))
        }

        // 3. 并发去重：若当前已有相同请求在网络飞行中，复用该 Promise
        if (inflightRequests.has(cacheKey)) {
            return inflightRequests.get(cacheKey).then(res => JSON.parse(JSON.stringify(res)))
        }

        // 4. 发起实际网络请求
        const requestPromise = zzquantClient(config)
            .then(res => {
                // 缓存有效响应
                if (res && res.data && (res.data.code === 20000 || res.data.code === 200)) {
                    const expireAt = Date.now() + ttl
                    apiCache.set(cacheKey, { expireAt, data: res })
                    setStoredCache(cacheKey, res, expireAt)
                }
                return res
            })
            .finally(() => {
                inflightRequests.delete(cacheKey)
            })

        inflightRequests.set(cacheKey, requestPromise)
        return requestPromise
    }

    return zzquantClient(config)
}

// ==========================================
// 4. Local 客户端单例 (http://127.0.0.1:9001)
// ==========================================
const localClient = axios.create({
    timeout: 30000,
    baseURL: 'http://127.0.0.1:9001'
})
localClient.interceptors.request.use(
    config => {
        try {
            const token = localStorage.getItem('token')
            if (token) {
                if (!config.headers) config.headers = {}
                config.headers['Authorization'] = `Bearer ${token}`
            }
        } catch (e) {
            console.error(e)
        }
        return config
    },
    err => {
        console.log(err)
        return Promise.reject(err)
    }
)
localClient.interceptors.response.use(
    res => res,
    err => {
        console.log(err)
        return Promise.reject(err)
    }
)

export function request_local(config) {
    return localClient(config)
}
